import { Group, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * The top of a page: what this screen is, one line on why you'd be here, and
 * the actions that belong to the screen as a whole.
 *
 * Extracted because 64 route files hand-roll `<Title order={2}>` plus an
 * optional dimmed sentence plus an ad-hoc action `<Group>`, each with slightly
 * different spacing and heading level. The drift is invisible on any one page
 * and obvious when you move between two.
 *
 * `status` sits next to the title rather than in `actions` on purpose: a badge
 * that describes the page's subject ("Connected", "3 need attention") is part
 * of the identity of the screen, not something you click.
 */
export interface PageHeaderProps {
  title: string;
  /** One sentence, second person, on what this screen is for. */
  description?: string;
  /** Rendered inline after the title — a badge or pill describing the subject. */
  status?: ReactNode;
  /** Buttons belonging to the whole screen. Primary action last, as Mantine reads left-to-right. */
  actions?: ReactNode;
}

export function PageHeader({ title, description, status, actions }: PageHeaderProps) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Stack gap={2}>
        <Group gap="sm" align="center">
          <Title order={2}>{title}</Title>
          {status}
        </Group>
        {description && (
          <Text c="dimmed" size="sm">
            {description}
          </Text>
        )}
      </Stack>
      {actions && (
        <Group gap="xs" wrap="nowrap">
          {actions}
        </Group>
      )}
    </Group>
  );
}
